<template>
  <div id="app" class="enterprise-platform">
    <!-- 动态星空背景 -->
    <div class="bg-canvas">
      <div v-for="n in 30" :key="n" class="star" :style="starStyle(n)"></div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 顶部导航 -->
    <header class="top-bar">
      <div class="logo">
        <div class="logo-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <span class="logo-text">时代<span class="highlight">高科</span></span>
      </div>
      <div class="datetime">
        <span class="time">{{ currentTime }}</span>
        <span class="date">{{ currentDate }}</span>
      </div>
    </header>

    <!-- 主内容区 -->
    <main class="main-content">
      <!-- 欢迎语 -->
      <div class="welcome-section">
        <h1 class="welcome-title">
          <span class="typing-text">{{ typingText }}</span>
          <span class="cursor">|</span>
        </h1>
        <p class="welcome-sub">企业管理系统集成平台 · 一站式智能入口</p>
      </div>

      <!-- 六宫格卡片 -->
      <div class="cards-grid">
        <div
          v-for="(app, index) in apps"
          :key="index"
          class="app-card"
          :class="{ active: activeCard === index }"
          @mouseenter="activeCard = index"
          @mouseleave="activeCard = null"
          @click="openApp(app.url)"
        >
          <!-- 卡片光效 -->
          <div class="card-glow"></div>

          <!-- 卡片内容 -->
          <div class="card-content">
            <div class="card-icon" :style="{ '--accent': app.color }">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  v-if="app.icon === 'users'"
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                />
                <circle v-if="app.icon === 'users'" cx="9" cy="7" r="4" />
                <path
                  v-if="app.icon === 'users'"
                  d="M23 21v-2a4 4 0 0 0-3-3.87"
                />
                <path
                  v-if="app.icon === 'users'"
                  d="M16 3.13a4 4 0 0 1 0 7.75"
                />

                <path v-if="app.icon === 'bar-chart'" d="M18 20V10" />
                <path v-if="app.icon === 'bar-chart'" d="M12 20V4" />
                <path v-if="app.icon === 'bar-chart'" d="M6 20v-6" />

                <path
                  v-if="app.icon === 'folder'"
                  d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
                />

                <path
                  v-if="app.icon === 'energy'"
                  d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
                />

                <path
                  v-if="app.icon === 'shopping-cart'"
                  d="M9 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                />
                <path
                  v-if="app.icon === 'shopping-cart'"
                  d="M20 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                />
                <path
                  v-if="app.icon === 'shopping-cart'"
                  d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"
                />

                <path
                  v-if="app.icon === 'settings'"
                  d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                />
                <path
                  v-if="app.icon === 'settings'"
                  d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
                />
              </svg>
            </div>
            <h3 class="card-title">{{ app.name }}</h3>
            <p class="card-desc">{{ app.desc }}</p>
            <div class="card-status">
              <span
                class="status-dot"
                :style="{ background: app.color }"
              ></span>
              <span class="status-text">运行中</span>
            </div>
          </div>

          <!-- 边框动画 -->
          <div class="card-border"></div>
        </div>
      </div>

      <!-- 底部信息 -->
      <footer class="bottom-info">
        <div class="stats-row">
          <div class="stat-item">
            <span class="stat-num">6</span>
            <span class="stat-label">接入系统</span>
          </div>
          <div class="stat-item">
            <span class="stat-num">99.9%</span>
            <span class="stat-label">系统可用性</span>
          </div>
          <div class="stat-item">
            <span class="stat-num">24/7</span>
            <span class="stat-label">全天候服务</span>
          </div>
        </div>
        <p class="copyright">
          © 2026 NexusHub Enterprise Platform · 企业管理系统集成平台
        </p>
      </footer>
    </main>
  </div>
</template>

<script>
export default {
  name: "HomePage",
  data() {
    return {
      currentTime: "",
      currentDate: "",
      activeCard: null,
      typingText: "",
      typingIndex: 0,
      typingTarget: "欢迎回来，管理员",
      apps: [
        {
          name: "OA办公系统",
          desc: "流程审批 · 公文管理 · 协同办公",
          url: "http://183.62.211.146:8081/eoffice/client/web/login",
          icon: "users",
          color: "#00d4ff",
        },
        {
          name: "ERP资源管理",
          desc: "供应链 · 生产制造 · 库存管理",
          url: "http://183.62.211.146:8089/k3cloud",
          icon: "bar-chart",
          color: "#7c3aed",
        },
        {
          name: "CRM客户管理",
          desc: "销售跟踪 · 客户画像 · 商机管理",
          url: "https://sso.tht-us.com/#/CRM",
          icon: "folder",
          color: "#f59e0b",
        },
        {
          name: "EMS管理系统",
          desc: "能量管理 · 实时监控 · 数据报表",
          url: "http://120.78.157.6/#/login",
          icon: "energy",
          color: "#10b981",
        },
        {
          name: "SRM供应链管理",
          desc: "供应商管理 · 采购订单 · 物流跟踪",
          url: "https://sso.tht-us.com/#/SRM",
          icon: "shopping-cart",
          color: "#ef4444",
        },
        {
          name: "MES执行系统",
          desc: "效率核查 · 质量保证 · 流程追溯",
          url: "https://sdgksjdmf.dfs.yelinksaas.com/#/home",
          icon: "settings",
          color: "#8b5cf6",
        },
      ],
    };
  },
  mounted() {
    this.updateTime();
    setInterval(this.updateTime, 1000);
    this.startTyping();
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString("zh-CN", { hour12: false });
      this.currentDate = now.toLocaleDateString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      });
    },
    startTyping() {
      const timer = setInterval(() => {
        if (this.typingIndex < this.typingTarget.length) {
          this.typingText += this.typingTarget[this.typingIndex];
          this.typingIndex++;
        } else {
          clearInterval(timer);
          setTimeout(() => {
            this.typingText = "";
            this.typingIndex = 0;
            this.startTyping();
          }, 3000);
        }
      }, 100);
    },
    openApp(url) {
      window.open(url, "_blank");
    },
    starStyle() {
      return {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${3 + Math.random() * 4}s`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
// ========== 变量定义 ==========
$bg-primary: #011039;
$bg-secondary: #0a1a4a;
$bg-card: rgba(255, 255, 255, 0.03);
$border-glow: rgba(0, 212, 255, 0.3);
$text-primary: #ffffff;
$text-secondary: rgba(255, 255, 255, 0.6);
$accent-cyan: #00d4ff;
$accent-purple: #7c3aed;

// ========== 动画关键帧 ==========
@keyframes twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.5);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.1);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 212, 255, 0.3);
  }
}

@keyframes border-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes cursor-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

// ========== 基础样式 ==========
.enterprise-platform {
  min-height: 100vh;
  background: $bg-primary;
  color: $text-primary;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  position: relative;
  overflow-x: hidden;
}

// ========== 动态背景 ==========
.bg-canvas {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  .star {
    position: absolute;
    width: 2px;
    height: 2px;
    background: white;
    border-radius: 50%;
    animation: twinkle ease-in-out infinite;
  }

  .grid-overlay {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(0, 212, 255, 0.03) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
  }
}

// ========== 顶部导航 ==========
.top-bar {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  backdrop-filter: blur(10px);
  background: rgba(1, 16, 57, 0.8);
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);

  .logo {
    display: flex;
    align-items: center;
    gap: 12px;

    .logo-icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, $accent-cyan, $accent-purple);
      border-radius: 10px;
      box-shadow: 0 0 20px rgba(0, 212, 255, 0.4);

      svg {
        width: 24px;
        height: 24px;
        color: white;
      }
    }

    .logo-text {
      font-size: 24px;
      font-weight: 700;
      letter-spacing: 2px;

      .highlight {
        background: linear-gradient(90deg, $accent-cyan, $accent-purple);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }
  }

  .datetime {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    .time {
      font-size: 28px;
      font-weight: 300;
      font-variant-numeric: tabular-nums;
      color: $accent-cyan;
      text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
    }

    .date {
      font-size: 13px;
      color: $text-secondary;
      letter-spacing: 1px;
    }
  }
}

// ========== 主内容区 ==========
.main-content {
  position: relative;
  z-index: 5;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 60px;
}

// ========== 欢迎语 ==========
.welcome-section {
  text-align: center;
  margin-bottom: 60px;

  .welcome-title {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 16px;
    min-height: 56px;

    .typing-text {
      background: linear-gradient(90deg, #ffffff, $accent-cyan, #ffffff);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: gradient-shift 3s linear infinite;
    }

    .cursor {
      color: $accent-cyan;
      animation: cursor-blink 1s step-end infinite;
    }
  }

  .welcome-sub {
    font-size: 16px;
    color: $text-secondary;
    letter-spacing: 4px;
    text-transform: uppercase;
  }
}

// ========== 六宫格卡片 ==========
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 60px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.app-card {
  position: relative;
  background: $bg-card;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px 30px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(0, 212, 255, 0.3);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(0, 212, 255, 0.1);

    .card-glow {
      opacity: 1;
    }

    .card-icon {
      transform: scale(1.1);
      box-shadow: 0 0 30px var(--accent);
    }

    .card-border::before {
      opacity: 1;
    }
  }

  .card-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
      rgba(0, 212, 255, 0.15) 0%,
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }

  .card-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .card-icon {
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 18px;
    margin-bottom: 20px;
    transition: all 0.4s;

    svg {
      width: 32px;
      height: 32px;
      color: var(--accent);
    }
  }

  .card-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 10px;
    color: $text-primary;
  }

  .card-desc {
    font-size: 13px;
    color: $text-secondary;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .card-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      box-shadow: 0 0 8px currentColor;
      animation: pulse-glow 2s ease-in-out infinite;
    }
  }

  .card-border {
    position: absolute;
    inset: 0;
    border-radius: 20px;
    overflow: hidden;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      inset: -2px;
      background: conic-gradient(
        from 0deg,
        transparent 0%,
        $accent-cyan 25%,
        transparent 50%,
        $accent-purple 75%,
        transparent 100%
      );
      animation: border-rotate 4s linear infinite;
      opacity: 0;
      transition: opacity 0.4s;
      z-index: -1;
    }

    &::after {
      content: "";
      position: absolute;
      inset: 1px;
      background: $bg-primary;
      border-radius: 19px;
      z-index: -1;
    }
  }
}

// ========== 底部信息 ==========
.bottom-info {
  text-align: center;

  .stats-row {
    display: flex;
    justify-content: center;
    gap: 60px;
    margin-bottom: 40px;

    @media (max-width: 600px) {
      flex-direction: column;
      gap: 20px;
    }
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .stat-num {
      font-size: 32px;
      font-weight: 700;
      background: linear-gradient(135deg, $accent-cyan, $accent-purple);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .stat-label {
      font-size: 13px;
      color: $text-secondary;
      letter-spacing: 2px;
    }
  }

  .copyright {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.2);
    letter-spacing: 1px;
  }
}
</style>
